/**
 * The board's token drawing: one unit's image in a pointy-top hex inscribed in its footprint, a thick
 * ring in its side colour, and the state marks around it (`projects/token-art.md` Phase 2,
 * `decisions/0184` §4, the hex silhouette from `projects/hex-tokens.md`). `pixiCombatBoard.ts` owns WHERE a token is and WHEN it is rebuilt; this
 * module owns only what goes inside the token's container, in footprint-local pixels
 * (`0..side` on both axes).
 *
 * **The hex is a little bigger than the footprint** (`hexGeometry.ts`'s `TOKEN_SCALE`, `decisions/0197` as
 * amended 2026-09-29), so a Size 1 token overflows its cell; the label solver pads its token rect by
 * that overflow (`hexGeometry.ts`'s `tokenOverflow`). The "mine" ring sits inside the ring. The hex
 * sits inside the band `pixiTokenChrome.ts` draws its vitals trim in ({@link tokenHex}), so the
 * ring's outer edge stays put when the ring thickens for a selection.
 *
 * **Loading.** The fallback (the side-coloured hex with a generic silhouette, or the role glyph)
 * is drawn synchronously on every build, so a token is never an empty slot. The image comes from
 * Pixi `Assets` (its own cache, keyed by URL); a texture already in that cache is drawn in the same
 * pass, otherwise the load resolves later and the sprite is added over the fallback then — unless
 * the token was rebuilt or destroyed in the meantime, which the art layer's `destroyed` flag says.
 * A failed load (a pruned upload, a 404) leaves the fallback in place. The HTTP cache behind
 * `Assets` is warmed ahead of time by `lib/assetPrecache.ts`.
 */
import { Assets, BlurFilter, ColorMatrixFilter, Container, Graphics, Sprite, Text, TextStyle, Texture } from "pixi.js";
import type { TokenImage } from "../../lib/tokenArt";
import { CELL } from "./combatCells";
import { dashedHex, growHex, hexPoints, tokenHex } from "./hexGeometry";

/** Where textures come from. The default is Pixi `Assets`; tests inject a fake. */
export type TokenTextureSource = {
  /** A texture already decoded and cached for `url`, or `undefined`. */
  peek: (url: string) => Texture | undefined;
  load: (url: string) => Promise<Texture>;
};

/**
 * Token art is 600–1400 px square and a token is drawn at 40 world px, so at the board's usual zoom
 * it is minified 20× or more. Without mipmaps that samples a scatter of source pixels and the art
 * looks noisy; with them it costs a third more texture memory per image. The hex-cropped sprite
 * shares this source, so it inherits them.
 */
export const pixiAssetsSource: TokenTextureSource = {
  peek: (url) => (Assets.cache.has(url) ? Assets.get<Texture>(url) : undefined),
  load: (url) => Assets.load<Texture>({ src: url, data: { autoGenerateMipmaps: true } }),
};

/** Everything one token's drawing depends on. `accent`/`fill` are the side colours (`tokenLook`). */
export type TokenSpriteSpec = {
  side: number;
  image: TokenImage;
  accent: string;
  fill: string;
  selected: boolean;
  /** The viewer controls this unit — a second, thin inner ring. */
  mine: boolean;
  destroyed: boolean;
  /** RAW `Hidden` — the holo-ghost look: the side ring draws dashed and the face reads as a sensor
   *  return ({@link applyHoloGhost}). */
  hidden: boolean;
  concealed: boolean;
  openDoor: boolean;
  /** The unit went this round: its side ring and glow turn grey (`pixiTokenChrome.ts`'s peak check
   *  says the same); the art is not dimmed. */
  spent?: boolean;
};

/** What a drawn token is showing, for the board's debug hook and tests. `loaded` flips once the
 *  sprite is on screen; it stays `false` for a generic token, which has no image. */
export type TokenArtState = { tier: TokenImage["tier"]; url: string | null; loaded: boolean };

/** The side ring, thin so the art reads (developer, 2026-09-29: "make the blue border thinner"). */
const RING_WIDTH = 1.5;
const RING_WIDTH_SELECTED = 2.5;
/** The side glow under the ring, scaled with it. */
const GLOW_WIDTH = 3;
const GLOW_WIDTH_SELECTED = 5;
const MINE_RING_WIDTH = 1;
/** A spent unit's ring: `pixiTokenChrome.ts`'s `SPENT_GREY`. */
const SPENT_RING = "#8d9199";
/** The "mine" ring's gap inside the side ring. */
const MINE_RING_GAP = 2;
/** Backing under the art and the fallback, so a transparent PNG or a translucent side fill never
 *  shows the terrain through the token. */
const TOKEN_BACKING = 0x141312;
/**
 * How far a {@link TokenImage.framed} token is zoomed before the hex crop. The vendored tokens put
 * the art inside a red hexagon whose inner edge sits at about 0.83 of the half-width, so 1.2× pushes
 * the frame just outside the crop.
 */
const FRAMED_ZOOM = 1.2;
const FALLBACK_ALPHA = 0.6;
/** How far above centre a Size 2+ token's role glyph sits, as a fraction of the hex's radius: the
 *  unit's name label sits inside its lower half, as high as the HP trim along the lower edges pushes
 *  it (`hexGeometry.ts`'s `insideLabelFloor`). */
const BIG_GLYPH_LIFT = 0.3;
/** The holo-ghost tint for a RAW `Hidden` token (`decisions/0188`, the developer's 2026-09-25 pick). */
const HOLO_TINT = 0x7fe8ff;
const HOLO_ART_ALPHA = 0.7;
const HOLO_FALLBACK_ALPHA = 0.6;
/** Scanline pitch and thickness in footprint pixels. */
const HOLO_SCANLINE_PITCH = 3;
const HOLO_SCANLINE_WIDTH = 1.2;
const HOLO_SCANLINE_ALPHA = 0.45;
const HOLO_GLOW_ALPHA = 0.12;

/** Draw one token into `container` (which the caller has cleared). Returns the art state, which
 *  is mutated to `loaded: true` if the image arrives later. */
export function drawTokenSprite(container: Container, spec: TokenSpriteSpec, source: TokenTextureSource = pixiAssetsSource): TokenArtState {
  const { side, image, accent } = spec;
  const ringColor = spec.spent ? SPENT_RING : accent;
  const c = side / 2;
  const ringWidth = spec.selected ? RING_WIDTH_SELECTED : RING_WIDTH;
  // The ring's centreline: its outer edge is fixed by the hex layout, whatever its width.
  const r = growHex(tokenHex(side, CELL).ringOuter, -ringWidth / 2);
  const state: TokenArtState = { tier: image.tier, url: image.url, loaded: false };

  if (!spec.destroyed) {
    const glow = hexPath(new Graphics(), c, r).stroke({ width: spec.selected ? GLOW_WIDTH_SELECTED : GLOW_WIDTH, color: ringColor, alpha: 0.4 });
    glow.filters = [new BlurFilter({ strength: spec.selected ? 5 : 3 })];
    container.addChild(glow);
  }
  container.addChild(hexPath(hexPath(new Graphics(), c, r).fill(TOKEN_BACKING), c, r).fill(spec.fill));

  const fallback = buildFallback({ c, r, accent, glyph: image.glyph, lift: side > CELL ? r * BIG_GLYPH_LIFT : 0 });
  container.addChild(fallback);

  const art = new Container();
  container.addChild(art);
  if (image.url !== null) {
    const url = image.url;
    const attach = (tex: Texture): void => {
      if (art.destroyed) return;
      art.addChild(...hexCroppedSprite(tex, { c, r, zoom: image.framed ? FRAMED_ZOOM : 1 }));
      fallback.visible = false;
      state.loaded = true;
    };
    const cached = source.peek(url);
    if (cached) attach(cached);
    else source.load(url).then(attach, () => {});
  }

  if (spec.hidden) applyHoloGhost(container, { art, fallback, c, r });

  const ring = new Graphics();
  if (spec.hidden) for (const [x1, y1, x2, y2] of dashedHex(c, r, { dash: 4, gap: 4 })) ring.moveTo(x1, y1).lineTo(x2, y2);
  else hexPath(ring, c, r);
  ring.stroke({ width: ringWidth, color: ringColor, join: "miter" });
  container.addChild(ring);

  if (spec.mine && !spec.destroyed) {
    container.addChild(hexPath(new Graphics(), c, growHex(r, -ringWidth / 2 - MINE_RING_GAP)).stroke({ width: MINE_RING_WIDTH, color: accent, join: "miter" }));
  }
  if (spec.concealed) container.addChild(dashedSquare({ side, inset: 1, dash: 5, gap: 4, width: 1.2, color: accent }));
  if (spec.openDoor) container.addChild(dashedSquare({ side, inset: 1, dash: 3, gap: 3, width: 1, color: accent }));
  if (spec.destroyed) {
    const x = new Text({ text: "×", style: new TextStyle({ fill: accent, fontFamily: "JetBrains Mono, monospace", fontWeight: "700", fontSize: Math.min(side * 0.5, 22) }) });
    x.anchor.set(0.5);
    x.position.set(c, c);
    container.addChild(x);
  }
  return state;
}

/**
 * The **holo ghost** for a RAW `Hidden` token: the art is desaturated and tinted cyan, dimmed a
 * little, and cut by dark horizontal scanlines over a faint cyan glow, all inside the hex. The token
 * container itself stays at full opacity so Hidden never reads as the Activated dim; the side colour
 * stays on the (dashed) ring so friend and foe still read. Static — no ticker.
 */
function applyHoloGhost(container: Container, opts: { art: Container; fallback: Container; c: number; r: number }): void {
  const { art, fallback, c, r } = opts;
  const grey = new ColorMatrixFilter();
  grey.desaturate();
  const tint = new ColorMatrixFilter();
  tint.tint(HOLO_TINT, true);
  art.filters = [grey, tint];
  art.alpha = HOLO_ART_ALPHA;
  fallback.alpha = HOLO_FALLBACK_ALPHA;

  const lines = new Graphics();
  for (let y = c - r; y < c + r; y += HOLO_SCANLINE_PITCH) lines.rect(c - r, y, 2 * r, HOLO_SCANLINE_WIDTH);
  lines.fill({ color: TOKEN_BACKING, alpha: HOLO_SCANLINE_ALPHA });
  const glow = hexPath(new Graphics(), c, r).fill({ color: HOLO_TINT, alpha: HOLO_GLOW_ALPHA });
  const overlay = new Container();
  overlay.label = "holo-ghost";
  overlay.addChild(glow, lines);
  const clip = hexPath(new Graphics(), c, r).fill(0xffffff);
  overlay.mask = clip;
  container.addChild(overlay, clip);
}

/** The fallback face: the role glyph when there is one, else a generic head-and-shoulders
 *  silhouette. It says "a unit is here" without saying which. */
function buildFallback(opts: { c: number; r: number; accent: string; glyph: string | undefined; lift: number }): Container {
  const { c, r, accent, glyph } = opts;
  if (glyph) {
    const t = new Text({ text: glyph, style: new TextStyle({ fill: accent, fontFamily: "JetBrains Mono, monospace", fontWeight: "700", fontSize: Math.max(8, Math.min(r * 0.62, 16)) }) });
    t.anchor.set(0.5);
    t.position.set(c, c - opts.lift);
    return t;
  }
  const g = new Graphics()
    .circle(c, c - r * 0.2, r * 0.24)
    .fill({ color: accent, alpha: FALLBACK_ALPHA })
    .ellipse(c, c + r * 0.52, r * 0.52, r * 0.36)
    .fill({ color: accent, alpha: FALLBACK_ALPHA });
  // Keep the shoulders inside the hex.
  const clip = hexPath(new Graphics(), c, r).fill(0xffffff);
  const wrap = new Container();
  wrap.addChild(g, clip);
  g.mask = clip;
  return wrap;
}

/**
 * A sprite cover-fitted to the hex of circumradius `r` about `(c,c)`, zoomed by `zoom`, and its hex
 * mask. Unzoomed art is top-aligned to the hex; framed (zoomed) art stays centred. The sprite gets its OWN `Texture` over the cached source: `pixiCombatBoard`'s `destroy()`
 * tears the stage down with `texture: true`, which would otherwise destroy the `Assets`-cached
 * texture itself and hand the next board mount a dead one.
 */
function hexCroppedSprite(tex: Texture, geo: { c: number; r: number; zoom: number }): [Sprite, Graphics] {
  const { c, r, zoom } = geo;
  const sprite = new Sprite(new Texture({ source: tex.source, frame: tex.frame.clone() }));
  const scale = (2 * r * zoom) / Math.max(1, Math.min(tex.width, tex.height));
  sprite.scale.set(scale);
  if (zoom === 1) {
    // Top-aligned: the art's top edge sits on the hex's top vertex, so a tall portrait loses its
    // feet, never its head.
    sprite.anchor.set(0.5, 0);
    sprite.position.set(c, c - r);
  } else {
    // A framed token is zoomed evenly about its centre, pushing its own frame out on every side.
    sprite.anchor.set(0.5);
    sprite.position.set(c, c);
  }
  const mask = hexPath(new Graphics(), c, r).fill(0xffffff);
  sprite.mask = mask;
  return [sprite, mask];
}

/** Add the closed hex of circumradius `r` about `(c,c)` to `g`'s path; the caller fills or strokes. */
function hexPath(g: Graphics, c: number, r: number): Graphics {
  return g.poly(hexPoints(c, c, r), true);
}

/** A dashed square just inside the footprint box — the concealed / open-door mark. */
function dashedSquare(opts: { side: number; inset: number; dash: number; gap: number; width: number; color: string }): Graphics {
  const { side, inset, dash, gap } = opts;
  const g = new Graphics();
  const a = inset;
  const b = side - inset;
  const edges: [number, number, number, number][] = [
    [a, a, b, a],
    [b, a, b, b],
    [b, b, a, b],
    [a, b, a, a],
  ];
  for (const [x1, y1, x2, y2] of edges) {
    const len = Math.hypot(x2 - x1, y2 - y1);
    const ux = (x2 - x1) / len;
    const uy = (y2 - y1) / len;
    for (let pos = 0; pos < len; pos += dash + gap) {
      const end = Math.min(pos + dash, len);
      g.moveTo(x1 + ux * pos, y1 + uy * pos).lineTo(x1 + ux * end, y1 + uy * end);
    }
  }
  return g.stroke({ width: opts.width, color: opts.color });
}
